/**
 * DataQualityBadge — small pill that surfaces the dataset behind any
 * sold-price card so buyers know our numbers aren't pure hype.
 *
 * Cross-market by design — picks the right data source from useMarket():
 *   IE → /api/ppr/stats              → "12,340 PPR sales · 26 counties …"
 *   UK → /api/uk/sold-prices/stats   → "60k HM Land Registry sales · 2,220 …"
 *   US → (stub — renders nothing until US sold-price stats endpoint is wired)
 *
 * Accepts an optional `market` prop ('IE'|'UK'|'GB'|'US') purely for callers
 * that want to force a specific market (e.g. when rendering a UK card on
 * the IE site). Defaults to whatever useMarket() resolves.
 *
 * Fails silently — if stats fetch errors, badge renders nothing rather
 * than breaking the page.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Database, CheckCircle2 } from 'lucide-react';
import { useMarket } from '../hooks/useMarket';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Per-market config keyed by canonical code. Adding USA later = one new
// entry here + a /api/us/sold-prices/stats endpoint.
const ENDPOINTS = {
  GB: {
    url: '/api/uk/sold-prices/stats',
    sourceShort: 'HM Land Registry',
    unit: 'sales',
    granularityKey: 'postcode_areas',
    granularityLabel: 'postcodes',
  },
  IE: {
    url: '/api/ppr/stats',
    sourceShort: 'PPR',
    unit: 'sales',
    granularityKey: 'counties',
    granularityLabel: 'counties',
  },
};

const fmtCount = (n) => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(0)}k`;
  return String(n);
};

const fmtMonth = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  } catch { return null; }
};

const DataQualityBadge = ({ market: marketOverride, className = '' }) => {
  // Resolve the canonical market code. `marketOverride` accepts 'UK', 'IE',
  // 'GB', etc. — useMarket() normalises them. When omitted we follow the
  // ambient market (homepage geo bucket / URL ?country=).
  const { code } = useMarket(marketOverride);
  const cfg = ENDPOINTS[code];
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!cfg) { setStats(null); return; }
    let cancelled = false;
    axios.get(`${BACKEND_URL}${cfg.url}`)
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [cfg]);

  // USA / unknown markets — no public stats endpoint yet, render nothing.
  if (!cfg || !stats || !stats.total_rows) return null;

  const refreshed = fmtMonth(stats.last_run_at);
  // Lowercase code for data-testid: 'gb' / 'ie' / 'us' — preserves the
  // existing 'data-quality-badge-uk' selector via an alias when code='GB'.
  const testIdSuffix = code === 'GB' ? 'uk' : code.toLowerCase();

  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[11px] text-emerald-900 bg-emerald-50/70 border border-emerald-200 rounded-full px-2.5 py-1 ${className}`}
      data-testid={`data-quality-badge-${testIdSuffix}`}
      title={`Source: ${stats.source}. Licence: ${stats.licence}.`}
    >
      <Database className="w-3 h-3 flex-shrink-0" />
      <span>
        <strong>{fmtCount(stats.total_rows)} {cfg.sourceShort} {cfg.unit}</strong>
        {stats[cfg.granularityKey] ? (
          <> · {fmtCount(stats[cfg.granularityKey])} {cfg.granularityLabel}</>
        ) : null}
        {refreshed ? (
          <> · refreshed {refreshed}</>
        ) : null}
      </span>
      <CheckCircle2 className="w-3 h-3 flex-shrink-0 text-emerald-600" />
    </div>
  );
};

export default DataQualityBadge;
